import React, { useState, useEffect, useMemo } from "react";

import "./PointsPredictionTOTW.css";
import Checkbox from "../../components/Checkbox/Checkbox";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRetweet} from "@fortawesome/free-solid-svg-icons";
import TOTWHistoryMenu from "./TOTWHistoryMenu";

const PointsPredictionTOTWContainer = (props) => {
    const [mainData, setMainData] = useState([]);
    const [livePoints, setLivePoints] = useState(null);
    const [predictedPoints, setPredictedPoints] = useState(null);
    // A finished gameweek picked from the history menu; only affects this card.
    const [pastGameweek, setPastGameweek] = useState(null);

    const sub = (<FontAwesomeIcon icon={faRetweet} style={{color: "#0f3a52", fontSize: 14}}/>)

    const homeAwayMap = {
        "home": "(H)",
        "away": "(A)"
    }

    const positionMap = {
        1: "GKP",
        2: "DEF",
        3: "MID",
        4: "FWD"
    }

    const updateGameweekData = (gw) => {
        if (!props.dataLoading && props.data && props.data[gw]) {
            let thisGameweekData = props.data[gw];
            let data = thisGameweekData["squad"].map((d, idx) => {
                return {
                    "data": d["data"],
                    "player_id": d['code'],
                    "team_id": d["team_code"],
                    "team_name": d["team_name"],
                    "player_position": positionMap[d['element_type']],
                    "price": d["now_cost"]/10,
                    "playerName": d["web_name"],
                    "status": d['status'],
                    "fixture": Array.isArray(d["opponent_name"])
                            ? d["opponent_name"].map((opp, idx) => opp + homeAwayMap[d["home_away"][idx]])
                            : [d["opponent_name"] + homeAwayMap[d["home_away"]]],
                    "pred": d["total_points"],
                    "role": d["role"],
                    "sub_status": d["sub_status"],
                    "live_points": Array.isArray(d["live_points"]) ? d["live_points"] : [d["live_points"]]
                }
            });
            setLivePoints(thisGameweekData['live_points']);
            setPredictedPoints(thisGameweekData['predicted_points'].toFixed(1));
            setMainData(data);
        }
    };

    // Picking a gameweek in the page selector brings the card back to the upcoming weeks.
    useEffect(() => {
        setPastGameweek(null);
    }, [props.gameweek]);

    const shownGameweek = pastGameweek ?? props.gameweek;

    useEffect(() => {
        updateGameweekData(shownGameweek);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data, props.dataLoading, shownGameweek]);

    // Team of the Week gameweeks without player predictions: the finished ones.
    const pastWeeks = useMemo(() => {
        if (!props.data) return [];
        const upcoming = props.availableGameweeks || [];
        return Object.keys(props.data).map(Number)
            .filter(gw => !upcoming.includes(gw) && gw < Math.min(...upcoming))
            .sort((a, b) => b - a)
            .map(gw => ({gameweek: gw, points: props.data[gw].live_points}));
    }, [props.data, props.availableGameweeks]);

    // --- Data Processing for Lineup ---
    // Filter players into starters and bench
    const starters = mainData.filter(p => ["Starter", "Captain", "Vice-captain"].includes(p.role));
    const benchPlayers = mainData.filter(p => !["Starter", "Captain", "Vice-captain"].includes(p.role));

    // Group starters by position
    const goalkeepers = starters.filter(p => p.player_position === 'GKP');
    const defenders = starters.filter(p => p.player_position === 'DEF');
    const midfielders = starters.filter(p => p.player_position === 'MID');
    const forwards = starters.filter(p => p.player_position === 'FWD');

    // Sort bench players to the correct order: GKP, 1, 2, 3
    const benchOrder = { 'Bench GKP': 0, 'Bench 1': 1, 'Bench 2': 2, 'Bench 3': 3 };
    benchPlayers.sort((a, b) => benchOrder[a.role] - benchOrder[b.role]);

    // Helper function to render a player box
    const renderPlayer = (player) => {
        let image_url = `url(https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${player.team_id}-110.png)`
        if (player['player_position'] === "GKP") {
            image_url = `url(https://fantasy.premierleague.com/dist/img/shirts/standard/shirt_${player.team_id}_1-110.png)`
        }

        let class_name = benchPlayers.includes(player) ? "bench-player-box" : "player-box"
        let name_length = player.playerName.length;
        let is_bench = benchPlayers.includes(player);

        // Calculate font size based on name length and player type
        let fontSize;
        if (name_length > 10) {
            fontSize = is_bench ? '9px' : '10.4px'; // 80% of 11px and 13px respectively
        }
        if (name_length > 12) {
            fontSize = is_bench ? '8px' : '10px'; // 80% of 11px and 13px respectively
        }

        const smallFontSize = is_bench ? '7px' : '8px';

        return (
            <div
                key={player.player_id}
                className={`${class_name} ${props.onPlayerClick ? "clickable" : ""}`}
                {...(props.onPlayerClick && {
                    role: "button",
                    tabIndex: 0,
                    "aria-label": `Show ${player.playerName}'s predicted points`,
                    onClick: () => props.onPlayerClick({code: player.player_id, name: player.playerName}),
                    onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            props.onPlayerClick({code: player.player_id, name: player.playerName});
                        }
                    },
                })}
                style={{backgroundImage: image_url}}
            >
                {player.role === 'Captain' && (<div className="captain-icon">C</div>)}
                {player.role === 'Vice-captain' && (<div className="captain-icon">V</div>)}
                {['Subbed On', 'Subbed Off'].includes(player.sub_status)
                    && (<div className="sub-icon">{sub}</div>)}
                <div className="player-jersey-space"></div>
                <div className="player-name" style={fontSize ? {fontSize} : {}}>{player.playerName}</div>
                <div className="player-fixture">
                    {player.live_points.map((points, index) => {
                        const isFixture = player.fixture.length >= 2 && points === "TBD";
                        return (
                            <span key={index} style={isFixture ? {fontSize: smallFontSize} : {}}>
                                {points === "TBD" ? player.fixture[index] : (player.role === 'Captain' ? points * 2 : points)}
                                {index < player.live_points.length - 1 && ', '}
                            </span>
                        );
                    })}
                </div>
            </div>
        )
    }


    const lineup = (
        <div style={{display: "flex", flexDirection: "column", alignItems: "center", width: "100%"}}>
            <div className={"title-container noSelect"}>
                <div className={"font-totw-title"}>{props.title ?? "Team of the Week"}</div>
                <TOTWHistoryMenu pastWeeks={pastWeeks}
                                 viewing={pastGameweek}
                                 upcomingGameweek={props.gameweek}
                                 onSelect={setPastGameweek}/>
                {livePoints > 0 ?
                    <div className={"font-totw-title"}>Points: <span className="totw-stat-value">{livePoints}</span></div> :
                    <div className={"font-totw-title"}>xPoints: <span className="totw-stat-value">{predictedPoints}</span></div>}
            </div>
            <div className={"player-totw-container noSelect"}>
                <div className="pitch">
                    <div className="pitch-row gkp-row">
                        {goalkeepers.map(renderPlayer)}
                    </div>

                    <div className="pitch-row def-row">
                        {defenders.map(renderPlayer)}
                    </div>

                    <div className="pitch-row mid-row">
                        {midfielders.map(renderPlayer)}
                    </div>

                    <div className="pitch-row fwd-row">
                    {forwards.map(renderPlayer)}
                    </div>
                </div>

                <div className="bench">
                    <div className="pitch-row bench-row">
                        {benchPlayers.map(renderPlayer)}
                    </div>
                </div>
            </div>
        </div>
    );

    return lineup;
};

export default PointsPredictionTOTWContainer;

